/**
 * Feature-level Error Boundary Component
 * Catches errors within feature sections (Chat, Workflows, Marketplace, etc.)
 * without crashing the entire application. Reports errors to Sentry when available
 * and provides recovery options to the user.
 *
 * Must be a class component because React error boundaries require
 * componentDidCatch and getDerivedStateFromError lifecycle methods.
 *
 * @module components/feature-error-boundary
 */

import { Component } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sentry } from '@/lib/sentry'
import { IS_DEV } from '@/lib/env'

import type { ErrorInfo, ReactNode } from 'react'

/**
 * Props for the FeatureErrorBoundary component
 * Configures the boundary's display name, recovery behavior, and children
 */
interface FeatureErrorBoundaryProps {
  /**
   * Human-readable name of the feature section (e.g. "Chat", "Workflows")
   * Displayed in the fallback UI and included in Sentry error reports
   */
  featureName: string

  /**
   * Child components to render inside the error boundary
   * If any child throws during rendering, the fallback UI is shown instead
   */
  children: ReactNode
}

/**
 * Internal state for the error boundary
 * Tracks whether an error has been caught and stores it for display
 */
interface FeatureErrorBoundaryState {
  /**
   * Whether an error has been caught by this boundary
   * When true, the fallback UI is rendered instead of children
   */
  hasError: boolean

  /**
   * The caught error object, if any
   * Used to display error details in development mode
   */
  error: Error | null
}

/**
 * Feature-scoped error boundary that isolates failures to individual sections.
 * Unlike the root-level Sentry boundary, this renders an inline recovery card
 * so the rest of the application (sidebar, header, other features) remains usable.
 */
export class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  /**
   * Derives error state from a thrown error during rendering.
   * Called by React when a child component throws during render.
   */
  static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    return { hasError: true, error }
  }

  /**
   * Reports the caught error to Sentry with feature context.
   * Adds the feature name as a tag so errors can be filtered by section in Sentry.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope) => {
      scope.setTag('feature', this.props.featureName)
      scope.setExtra('componentStack', errorInfo.componentStack)
      Sentry.captureException(error)
    })
  }

  /**
   * Resets the error state so children are re-rendered.
   * Called when the user clicks "Try Again" in the fallback UI.
   */
  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center p-8 w-full min-h-[300px]">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {this.props.featureName} encountered an error
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Something went wrong in this section. The rest of the application is still available.
                  </p>
                </div>
                {IS_DEV && this.state.error && (
                  <pre className="rounded-md bg-muted p-3 text-xs text-destructive overflow-auto max-h-32">
                    {this.state.error.message}
                    {this.state.error.stack && (
                      <>
                        {'\n\n'}
                        {this.state.error.stack}
                      </>
                    )}
                  </pre>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={this.handleReset}
                    variant="default"
                    size="sm"
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Try Again
                  </Button>
                  <Button
                    onClick={() => window.location.reload()}
                    variant="outline"
                    size="sm"
                  >
                    Refresh Page
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
